import React from "react";

import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import BookmarkButton from "./BookmarkButton";
import CategoryBadges from "./CategoryBadges";
import {
  formatAccountLevelMentionsInText,
  formatLevelTags,
  formatRoleLensTags,
  formatSeniorityTags,
  LIBRARY_TYPE,
  META_CHIP_CLASS,
  readLibraryBrowseScrollTop,
  readLibraryReturnStateFromLocation,
  ROLE_LENS_CHIP_CLASS,
  type LibraryReturnState,
  type SponsorTip,
} from "../../Utils/sponsorTipsConstants";

type TipCardProps = {
  tip: SponsorTip;
  className?: string;
  compact?: boolean;
  suppressCategoryBadge?: boolean;
  fromLibrary?: LibraryReturnState;
  /** @deprecated Prefer fromLibrary */
  fromSubtab?: string;
  showBookmark?: boolean;
};

const TipCard: React.FC<TipCardProps> = ({
  tip,
  className = "",
  compact = false,
  suppressCategoryBadge = false,
  fromLibrary,
  fromSubtab,
  showBookmark = true,
}) => {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  if (!tip) return null;

  const libraryState: LibraryReturnState =
    fromLibrary ||
    (fromSubtab
      ? {
          ...readLibraryReturnStateFromLocation(searchParams),
          subtab: fromSubtab,
        }
      : readLibraryReturnStateFromLocation(searchParams));
  const base = orgSlug ? `/${orgSlug}/guidance-library` : "/guidance-library";
  const tipPath = `${base}/${tip.url_slug}`;
  const isFm = tip.tip_type === "failure_mode";
  const snippetSource = formatAccountLevelMentionsInText(
    tip.email_short || tip.body || ""
  );
  const snippet = snippetSource.slice(0, 150);
  const levels = formatLevelTags(tip);
  const seniorities = formatSeniorityTags(tip);
  const roleLens = formatRoleLensTags(tip);
  const showCategoryRow = !suppressCategoryBadge || isFm;
  const showLevelSeniority = roleLens.length === 0;

  const handleOpenTip = (e: React.MouseEvent) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    const scrollTop = readLibraryBrowseScrollTop();
    navigate(tipPath, {
      state: {
        fromLibrary: {
          ...libraryState,
          ...(scrollTop != null ? { scrollTop } : {}),
        },
      },
    });
  };

  return (
    <Link
      to={tipPath}
      onClick={handleOpenTip}
      className={`relative block rounded-lg border border-gray-200 bg-white p-3.5 transition-all hover:border-purple-300 hover:shadow-sm ${className}`}
    >
      {showBookmark ? (
        <div className="absolute right-3 top-3 z-10">
          <BookmarkButton slug={tip.url_slug} />
        </div>
      ) : null}
      <div className={`pr-9 ${compact ? "flex flex-col gap-3" : ""}`}>
        {showCategoryRow ? (
          <div className={compact ? "" : "mb-2"}>
            <CategoryBadges
              category={tip.category}
              isFm={isFm}
              suppressParent={suppressCategoryBadge}
            />
          </div>
        ) : null}
        <h3
          className={`${
            compact
              ? "text-xs font-semibold leading-snug text-gray-900"
              : LIBRARY_TYPE.cardTitle
          } line-clamp-2 block ${compact ? "" : "mb-1"}`}
        >
          {formatAccountLevelMentionsInText(tip.title)}
        </h3>
        {!compact ? (
          <p className={`${LIBRARY_TYPE.cardBody} mb-3 line-clamp-3`}>
            {snippet}
            {snippetSource.length > 150 ? "…" : ""}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-1.5 text-xs text-gray-500">
          {roleLens.map(r => (
            <span key={r} className={ROLE_LENS_CHIP_CLASS}>
              {r}
            </span>
          ))}
          {showLevelSeniority
            ? levels.map(l => (
                <span key={l} className={META_CHIP_CLASS}>
                  {l}
                </span>
              ))
            : null}
          {showLevelSeniority
            ? seniorities.map(s => (
                <span key={s} className={META_CHIP_CLASS}>
                  {s}
                </span>
              ))
            : null}
        </div>
      </div>
    </Link>
  );
};

export default TipCard;
